import React, { useState, useEffect } from 'react';
import { Button, Input, Select, ComboBox } from '@/components/ui';
import axios from 'axios';
import type { InvestibleStructure, ISCategory, PlanningPeriod, User, OwnerType } from '@/types';

interface InvestibleStructureFormProps {
  initialData?: InvestibleStructure | null;
  planningPeriods: PlanningPeriod[];
  onSubmit: (data: Partial<InvestibleStructure>) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const InvestibleStructureForm: React.FC<InvestibleStructureFormProps> = ({
  initialData,
  planningPeriods,
  onSubmit,
  onCancel,
  isLoading = false
}) => {
  const [users, setUsers] = useState<User[]>([]);
  const [manualEntries, setManualEntries] = useState<string[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'BUSINESS' as ISCategory,
    planningPeriodId: '',
    ownerId: '',
    ownerText: '',
    ownerType: 'USER' as OwnerType
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch users and manual entries with IS_OWNER_SCOPE on component mount
  useEffect(() => {
    const fetchOwners = async () => {
      setUsersLoading(true);
      try {
        const token = localStorage.getItem('auth_token');
        const response = await axios.get('/api/v1/user-scopes/suggestions?scope=IS_OWNER_SCOPE', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
          const suggestions = response.data.data.suggestions || [];

          // Extract USER type suggestions and convert to User format
          const userSuggestions = suggestions
            .filter((s: any) => s.type === 'USER')
            .map((s: any) => ({
              id: s.id,
              firstName: s.label.split(' ')[0],
              lastName: s.label.split(' ').slice(1).join(' '),
              email: s.email
            }));

          // Extract manual TEXT entries
          const manualSuggestions = suggestions
            .filter((s: any) => s.type === 'TEXT')
            .map((s: any) => s.label);

          setUsers(userSuggestions);
          setManualEntries(manualSuggestions);
        }
      } catch (error) {
        console.error('Failed to fetch IS owners:', error);
      } finally {
        setUsersLoading(false);
      }
    };

    fetchOwners();
  }, []);

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name,
        description: initialData.description || '',
        category: initialData.category,
        planningPeriodId: initialData.planningPeriodId,
        ownerId: initialData.ownerId || '',
        ownerText: initialData.ownerText || '',
        ownerType: initialData.ownerType || 'USER'
      });
    } else {
      // Set default planning period to active one
      const activePeriod = planningPeriods.find(p => p.isActive);
      if (activePeriod) {
        setFormData(prev => ({ ...prev, planningPeriodId: activePeriod.id }));
      }
    }
  }, [initialData, planningPeriods]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.length < 3) {
      newErrors.name = 'Name must be at least 3 characters';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Name must not exceed 100 characters';
    }

    if (!formData.planningPeriodId) {
      newErrors.planningPeriodId = 'Planning period is required';
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Description must not exceed 500 characters';
    }

    // Validate owner assignment consistency
    if (formData.ownerType === 'USER' && !formData.ownerId) {
      newErrors.ownerId = 'Please select a user or switch to manual entry';
    } else if (formData.ownerType === 'TEXT' && !formData.ownerText?.trim()) {
      newErrors.ownerText = 'Please enter owner information or select a user';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const submitData = {
      name: formData.name,
      description: formData.description || undefined,
      category: formData.category,
      planningPeriodId: formData.planningPeriodId,
      ownerType: formData.ownerType,
      ...(formData.ownerType === 'USER' 
        ? { ownerId: formData.ownerId || undefined, ownerText: undefined }
        : { ownerId: undefined, ownerText: formData.ownerText || undefined }
      )
    };

    onSubmit(submitData);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleOwnerChange = (value: string, type: 'USER' | 'TEXT') => {
    setFormData(prev => ({
      ...prev,
      ownerType: type,
      ownerId: type === 'USER' ? value : '',
      ownerText: type === 'TEXT' ? value : ''
    }));
    
    // Clear owner-related errors
    if (errors.ownerId || errors.ownerText) {
      setErrors(prev => ({ 
        ...prev, 
        ownerId: '', 
        ownerText: '' 
      }));
    }
  };

  const categoryOptions = [
    { value: 'BUSINESS', label: 'Business' },
    { value: 'TECH', label: 'Tech' },
    { value: 'SUPPORT', label: 'Support' }
  ];

  const planningPeriodOptions = planningPeriods.map(period => ({
    value: period.id,
    label: `${period.name} (${period.status.toLowerCase()})`
  }));

  // Prepare user options for ComboBox - includes both users and manual entries
  const userOptions = [
    // User suggestions
    ...users.map(user => ({
      id: user.id,
      label: `${user.firstName} ${user.lastName}`,
      email: user.email
    })),
    // Manual entry suggestions with indicator
    ...manualEntries.map(entry => ({
      id: entry, // Use the text itself as ID for manual entries
      label: entry,
      email: '(Previously used)'
    }))
  ];

  // Get current owner value for ComboBox
  const getCurrentOwnerValue = () => {
    if (formData.ownerType === 'USER' && formData.ownerId) {
      return formData.ownerId;
    } else if (formData.ownerType === 'TEXT' && formData.ownerText) {
      return formData.ownerText;
    }
    return '';
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <Input
            label="Structure Name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            error={errors.name}
            placeholder="e.g., Customer Experience Platform"
            required
            fullWidth
          />
        </div>

        <Select
          label="Planning Period"
          value={formData.planningPeriodId}
          onChange={(e) => handleInputChange('planningPeriodId', e.target.value)}
          options={planningPeriodOptions}
          error={errors.planningPeriodId}
          required
          fullWidth
          helperText="The planning period this structure belongs to"
        />

        <Select
          label="Category"
          value={formData.category}
          onChange={(e) => handleInputChange('category', e.target.value)}
          options={categoryOptions}
          error={errors.category}
          required
          fullWidth
          helperText="Business: Revenue-generating, Tech: Infrastructure, Support: Operational"
        />

        <div className="md:col-span-2 space-y-1.5">
          <ComboBox
            label="Owner"
            options={userOptions}
            value={getCurrentOwnerValue()}
            onChange={handleOwnerChange}
            placeholder="Select a user or enter manually..."
            allowFreeText={true}
            disabled={usersLoading}
            error={errors.ownerId || errors.ownerText}
            className="w-full"
          />
          <p className="text-xs text-muted-foreground">
            The person responsible for this investible structure. You can select from existing users or enter manually.
          </p>
        </div>

        <div className="md:col-span-2">
          <div className="space-y-1.5">
            <div className="flex items-baseline justify-between">
              <label className="block text-sm font-medium text-foreground">
                Description
              </label>
              <span className="text-xs tabular-nums text-muted-foreground">
                {formData.description.length}/500
              </span>
            </div>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Describe the purpose and scope of this investible structure..."
              className="block w-full min-h-[96px] rounded-md border bg-background px-3 py-2 text-sm text-foreground placeholder-muted-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent border-border hover:border-border/80"
            />
            {errors.description && (
              <p className="text-xs text-status-error font-medium">{errors.description}</p>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 pt-3 border-t border-border">
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          isLoading={isLoading}
          disabled={isLoading}
        >
          {initialData ? 'Update' : 'Create'} Structure
        </Button>
      </div>
    </form>
  );
};

export default InvestibleStructureForm;